import React, { useEffect, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { Button, CircularProgress } from '@mui/material';
import { Delete as DeleteIcon, Shield as ShieldIcon } from '@mui/icons-material';
import * as Icon from 'react-bootstrap-icons';
import PasskeyService from '../service/PasskeyService';

const PasskeyManagement = () => {
  const [currentPasskey, setCurrentPasskey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isAvailable, setIsAvailable] = useState(false);
  const [processing, setProcessing] = useState(false);

  // 初期化
  useEffect(() => {
    checkAvailability();
    loadCurrentPasskey();
  }, []);

  // 利用可能性チェック
  const checkAvailability = async () => {
    const available = PasskeyService.isAvailable();
    setIsAvailable(available);
  };

  // このブラウザに登録されているパスキーを取得
  const loadCurrentPasskey = async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await PasskeyService.listPasskeys();
      
      // localStorageに保存されたcredential_idを取得
      const storedCredentialId = localStorage.getItem('passkey_credential_id');
      
      if (!storedCredentialId) {
        // localStorageになければ未登録
        setCurrentPasskey(null);
        return;
      }
      
      // credential_idが一致するパスキーを探す
      const matchingPasskey = list.find(passkey => 
        passkey.credential_id === storedCredentialId
      );
      
      if (!matchingPasskey) {
        // DBには存在しないが、localStorageにある場合（削除済み）
        localStorage.removeItem('passkey_credential_id');
        setCurrentPasskey(null);
        return;
      }
      
      setCurrentPasskey(matchingPasskey);
    } catch (err) {
      console.error('Failed to load passkey:', err);
      setError('パスキー情報の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // パスキー登録
  const handleRegister = async () => {
    try {
      setProcessing(true);
      setError(null);
      setSuccess(null);
      const defaultName = PasskeyService.getDefaultDeviceName();
      await PasskeyService.register(defaultName || 'デバイス');
      setSuccess('パスキーを登録しました');
      await loadCurrentPasskey();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Registration failed:', err);
      setError(err.message || 'パスキーの登録に失敗しました');
    } finally {
      setProcessing(false);
    }
  };

  // パスキー削除
  const handleDelete = async () => {
    if (!currentPasskey) return;
    
    if (!window.confirm('このブラウザのパスキーを削除しますか？')) {
      return;
    }

    try {
      setProcessing(true);
      setError(null);
      setSuccess(null);
      await PasskeyService.deletePasskey(currentPasskey.credential_id);
      setSuccess('パスキーを削除しました');
      await loadCurrentPasskey();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Delete failed:', err);
      setError(err.message || 'パスキーの削除に失敗しました');
    } finally {
      setProcessing(false);
    }
  };

  // 利用不可の場合
  if (!isAvailable) {
    return (
      <Alert variant="warning">
        <Icon.ExclamationTriangle className="me-2" />
        このブラウザはパスキーに対応していません。Chrome、Safari、Edgeなどの最新ブラウザをご利用ください。
      </Alert>
    );
  }

  return (
    <div>
      {/* 成功メッセージ */}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
          <Icon.CheckCircle className="me-2" />
          {success}
        </Alert>
      )}

      {/* エラー表示 */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          <Icon.ExclamationCircle className="me-2" />
          {error}
        </Alert>
      )}

      {/* パスキー設定 */}
      {loading ? (
        <CircularProgress size={24} />
      ) : currentPasskey ? (
        <Button
          variant="outlined"
          color="error"
          onClick={handleDelete}
          disabled={processing}
          startIcon={processing ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {processing ? '削除中...' : 'パスキーを削除'}
        </Button>
      ) : (
        <Button
          variant="contained"
          color="primary"
          onClick={handleRegister}
          disabled={processing}
          startIcon={processing ? <CircularProgress size={20} /> : <ShieldIcon />}
        >
          {processing ? '登録中...' : 'パスキーを登録'}
        </Button>
      )}
    </div>
  );
};

export default PasskeyManagement;
